import { withOwnerPoints, privateTransitionWins } from '../utils/projectOwnerState';
import { isSharedProject, routeSharedEdit, stopSharing, lifecycleInProgress, setSharedCopyWaiting } from './sharedProjectWrite';
import { PERSONAL_FIELDS, canonical, ownerMirror } from './sharedProjectModel';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import type { Project, ProjectTask, ProjectPhase, SubTask, TaskStatus, ProjectFolder, ProjectWorkspace, WorkspaceMetrics, FolderMetrics } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import {
  createBlobSync, mergeCollection, mergeTombstones, pruneTombstones, tombstone, sameRecords,
  matchesCloud, sameTombstones,
  type Tombstones,
} from '../lib/syncBlob';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import type { UserProjectTemplate } from '../data/projectTemplates';
import { useActivityLogStore } from './activityLogStore';
import { formatDateToLocal } from '../utils/date';
import { appendActivity } from '../utils/projectActivity';
import * as reducers from './projectReducers';
import { calculateProjectProgress, type ProjectReducer, type ProjectReducerContext, type ProjectReduction } from './projectReducers';
import { useCollaborationStore } from './collaborationStore';
import { queueProjectActivity } from './projectActivityRemote';
import { convertLegacyMilestones, convertLegacyMilestonesIn } from '../utils/projectMilestones';

// ─── Supabase sync (sync epic) ──────────────────────────────────────────────
// Projects + folders + workspaces sync as ONE atomic blob per user
// (user_projects.data). Whole-store-blob keeps the deeply nested data and the
// cross-references consistent. A single store subscription auto-syncs on any
// data change, guarded so initialize()'s load doesn't echo back.

const USER_PROJECTS_TABLE = 'user_projects';
function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' ? (raw as Tombstones) : {};
}

function mergeProjects(local: Project[], cloud: Project[], deleted: Tombstones): Project[] {
  return mergeCollection(local, cloud, deleted).map(winner => {
    if (lifecycleInProgress(winner.id)) return local.find(project => project.id === winner.id) ?? winner;
    const copies = [local.find(project => project.id === winner.id), cloud.find(project => project.id === winner.id)];
    const mirrors = copies.filter((project): project is Project => !!project?.sharedMirror)
      .sort((a, b) => b.sharedMirror!.revision - a.sharedMirror!.revision);
    const latest = mirrors[0];
    const privateCopy = copies.filter((project): project is Project => !!project?.privateSince && !project.sharedMirror)
      .sort((a, b) => b.privateSince!.revision - a.privateSince!.revision || Number(!!a.privateSince!.pending) - Number(!!b.privateSince!.pending) || b.updatedAt.localeCompare(a.updatedAt))[0];
    if (privateCopy && (!latest || privateTransitionWins(privateCopy, latest.sharedMirror!.revision))) return privateCopy;
    if (!latest) return winner; // Keep ordinary private merges (including own-delete) unchanged.
    const points = withOwnerPoints(winner, ...copies);
    return { ...ownerMirror(latest, points, latest.sharedMirror!.revision), updatedAt: winner.updatedAt };
  });
}

// ⚠️ Read-modify-write, NOT a wholesale upsert. The old code PUT the entire local
// {projects, folders, workspaces}, so a device holding a stale cache silently
// deleted records it had never seen (diagnosed 2026-07-27 — see lib/syncBlob.ts).
// All three collections use UUIDs, so ONE tombstone map covers them.
const projectTransport = supabaseBlobTransport(USER_PROJECTS_TABLE);
const projectSync = createBlobSync({
  transport: projectTransport,
  owner: () => useProjectStore.getState().cachedUserId,
  onError: err => console.error('Failed to sync projects to Supabase:', err),
  build: (cloud) => {
    const { projects, folders, workspaces, templates, deleted } = useProjectStore.getState();
    const cloudTombs = readTombstones(cloud);
    const merged = pruneTombstones(mergeTombstones(deleted, cloudTombs));
    const arr = (v: any) => (Array.isArray(v) ? v : []);
    const nextProjects = mergeProjects(projects, arr(cloud?.projects), merged);
    const nextFolders = mergeCollection(folders, arr(cloud?.folders), merged);
    const nextWorkspaces = mergeCollection(workspaces, arr(cloud?.workspaces), merged);
    /**
     * ⚠️⚠️ **A COLLECTION MISSING FROM THIS FUNCTION IS DELETED ON EVERY SYNC.**
     * `build` returns the WHOLE blob, so anything not named here is simply
     * absent from the next write — the user's saved templates would vanish on
     * the next device, silently and without an error. Added with `templates`
     * on 2026-08-19; a test pins the round trip.
     */
    const nextTemplates = mergeCollection(templates, arr(cloud?.templates), merged);
    const mirrorsMatch = nextProjects.every(project => (!project.sharedMirror && !project.privateSince) || canonical(project) === canonical(arr(cloud?.projects).find((copy: Project) => copy.id === project.id)));
    if (mirrorsMatch && matchesCloud(
      [[nextProjects, cloud?.projects], [nextFolders, cloud?.folders],
       [nextWorkspaces, cloud?.workspaces], [nextTemplates, cloud?.templates]],
      merged, cloudTombs,
    )) return null;
    return {
      projects: nextProjects, folders: nextFolders, workspaces: nextWorkspaces,
      templates: nextTemplates, deleted: merged,
    };
  },
});

interface ProjectState {
  projects: Project[];
  folders: ProjectFolder[];
  workspaces: ProjectWorkspace[];
  /**
   * "My templates" — structures the user saved from their own projects.
   *
   * ⭐⭐ THEY LIVE IN THIS BLOB RATHER THAN A TABLE OF THEIR OWN, on purpose: a
   * new table needs an owner-run migration, and `user_projects.data` is plain
   * jsonb driven by `partialize`. They are also co-located with the projects
   * they came from, which is where they are made and deleted.
   * ⚠️⚠️ Anything added to this state must ALSO be named in the sync `build`,
   * both hydration paths and `partialize` — a collection missing from any of
   * them is dropped on the next write. See the note in `build`.
   */
  templates: UserProjectTemplate[];
  userLevel: number;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;
  /**
   * ⭐ `phases` and `tasks` are OPTIONAL overrides, added 2026-08-19 for project
   * templates. Omit them and a project is born with `DEFAULT_PHASES` and no
   * tasks, exactly as before — every existing caller is unaffected.
   */
  addProject: (
    project: Omit<Project, 'id' | 'createdAt' | 'updatedAt' | 'progress' | 'phases' | 'tasks'>
      & { phases?: ProjectPhase[]; tasks?: ProjectTask[] },
  ) => string;
  updateProject: (id: string, updates: Partial<Project>) => void;
  deleteProject: (id: string) => void;
  archiveProject: (id: string) => void;
  restoreProject: (id: string) => void;

  // Phase management
  addPhase: (projectId: string, phase: Omit<ProjectPhase, 'id' | 'order'>) => void;
  updatePhase: (projectId: string, phaseId: string, updates: Partial<ProjectPhase>) => void;
  deletePhase: (projectId: string, phaseId: string) => void;
  reorderPhases: (projectId: string, sourceIndex: number, targetIndex: number) => void;
  togglePhaseCollapse: (projectId: string, phaseId: string) => void;

  // Task management
  addTask: (projectId: string, task: Omit<ProjectTask, 'id' | 'order'>) => void;
  updateTask: (projectId: string, taskId: string, updates: Partial<ProjectTask>) => void;
  deleteTask: (projectId: string, taskId: string) => void;
  toggleTask: (projectId: string, taskId: string) => void;
  updateTaskStatus: (projectId: string, taskId: string, status: TaskStatus) => void;
  moveTask: (projectId: string, taskId: string, phaseId: string, status: TaskStatus) => void;
  reorderTasks: (projectId: string, phaseId: string, status: TaskStatus, sourceIndex: number, targetIndex: number) => void;

  // Subtask management
  updateSubTask: (projectId: string, taskId: string, subTaskId: string, updates: Pick<SubTask, 'title'>) => void;
  addSubTask: (projectId: string, taskId: string, title: string) => void;
  toggleSubTask: (projectId: string, taskId: string, subTaskId: string) => void;
  deleteSubTask: (projectId: string, taskId: string, subTaskId: string) => void;


  // Nested projects
  convertTaskToProject: (projectId: string, taskId: string) => string | null;
  getChildProjects: (projectId: string) => Project[];
  getParentProject: (projectId: string) => Project | undefined;

  // Folder management
  addFolder: (folder: Omit<ProjectFolder, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateFolder: (folderId: string, updates: Partial<ProjectFolder>) => void;
  deleteFolder: (folderId: string) => void;
  moveProjectToFolder: (projectId: string, folderId: string | undefined) => void;
  getProjectsByFolder: (folderId: string) => Project[];
  getFolderMetrics: (folderId: string) => FolderMetrics;
  getFoldersByWorkspace: (workspaceId: string) => ProjectFolder[];

  // Workspace management
  addWorkspace: (workspace: Omit<ProjectWorkspace, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateWorkspace: (workspaceId: string, updates: Partial<ProjectWorkspace>) => void;
  deleteWorkspace: (workspaceId: string) => void;
  moveProjectToWorkspace: (projectId: string, workspaceId: string | undefined) => void;
  moveFolderToWorkspace: (folderId: string, workspaceId: string | undefined) => void;
  getProjectsByWorkspace: (workspaceId: string) => Project[];
  getWorkspaceMetrics: (workspaceId: string) => WorkspaceMetrics;

  /**
   * Save a project's CURRENT structure as a reusable template.
   *
   * ⚠️ Captures phases, tasks and subtasks by title in their present order —
   * never completion, ids, dates, assignees or point records. A template is a
   * plan; carrying progress would credit work nobody did on the next project.
   * Returns the new template's id.
   */
  saveProjectAsTemplate: (projectId: string, name?: string) => string | null;
  deleteUserTemplate: (templateId: string) => void;
  renameUserTemplate: (templateId: string, name: string) => void;

  /** id → deletedAt across all four collections. Stops the union-on-write
   *  resurrecting anything the user deleted. */
  deleted: Tombstones;

  // Getters
  getActiveProjects: () => Project[];
  getArchivedProjects: () => Project[];
  getRootProjects: () => Project[];
  getTasksByPhaseAndStatus: (projectId: string, phaseId: string, status: TaskStatus) => ProjectTask[];
  setUserLevel: (level: number) => void;
}

// The subset of state that is persisted to storage (data only, no methods).
type ProjectStorePersisted = Pick<ProjectState, 'projects' | 'folders' | 'workspaces' | 'templates' | 'deleted' | 'userLevel' | 'cachedUserId'>;

// Default phases for new projects
const DEFAULT_PHASES: Omit<ProjectPhase, 'id' | 'order'>[] = [
  { title: 'Initiation', collapsed: false },
  { title: 'Research', collapsed: false },
  { title: 'Prototype', collapsed: false },
  { title: 'Test', collapsed: false },
  { title: 'Delivery', collapsed: false }
];

/** Snapshot mutable configuration and entropy once at the action boundary. */
export function reducerContext(userLevel: number, newId = ''): ProjectReducerContext {
  return {
    now: new Date().toISOString(), newId, userLevel, mint: 'mint',
    points: {
      project: calculateLevelBasedPoints(POINTS.PROJECT_COMPLETION, userLevel),
      task: calculateLevelBasedPoints(POINTS.PROJECT_TASK, userLevel),
      subTask: calculateLevelBasedPoints(POINTS.PROJECT_SUBTASK, userLevel),
    },
  };
}

/** Only explicit actions consume effects. Hydration and cloud merge keep raw setters. */
function publishReduction(
  set: (updater: (state: ProjectState) => Partial<ProjectState>) => void,
  projectId: string,
  before: Project,
  reduction: ProjectReduction,
): void {
  for (const warning of reduction.warnings) console.warn(warning);
  // History precedes the XP journal, as in the original nested wrappers.
  const events = reduction.project.id === projectId
    ? reduction.events.map(event => ({ ...event, id: crypto.randomUUID() })) : [];
  if (events.length) {
    const sharing = useCollaborationStore.getState?.()?.sharedMeta?.[projectId];
    if (sharing?.isOwner) {
      queueProjectActivity(projectId, sharing.ownerId, [...(before.activity ?? []), ...events]);
    } else {
      set(state => ({ projects: state.projects.map(project => project.id === projectId
        ? { ...project, activity: appendActivity(project, events) } : project) }));
    }
  }
  if (reduction.mints.length) {
    const add = useActivityLogStore.getState().addActivity;
    for (const mint of reduction.mints) void add({ activityType: 'project', ...mint });
  }
}

function applyProjectReducer<A>(
  set: (updater: (state: ProjectState) => Partial<ProjectState>) => void,
  get: () => ProjectState,
  projectId: string,
  reducer: ProjectReducer<A>,
  args: A,
  newId = '',
): void | Promise<void> {
  if (isSharedProject(projectId)) {
    const personal = reducer === reducers.moveProjectToFolder || reducer === reducers.moveProjectToWorkspace;
    if (!personal && reducer === reducers.updateProject) {
      const updates = args as Partial<Project>;
      const privateUpdates = Object.fromEntries(Object.entries(updates).filter(([key]) => key !== 'activity' && key !== 'sharedMirror' && PERSONAL_FIELDS.includes(key as typeof PERSONAL_FIELDS[number])));
      const content = Object.fromEntries(Object.entries(updates).filter(([key]) => !PERSONAL_FIELDS.includes(key as typeof PERSONAL_FIELDS[number])));
      if (useCollaborationStore.getState().sharedMeta[projectId]?.isOwner && Object.keys(privateUpdates).length) {
        set(state => ({ projects: state.projects.map(project => project.id === projectId ? { ...project, ...privateUpdates } : project) }));
      }
      if (!Object.keys(content).length) return;
      return routeSharedEdit(projectId, reducers.updateProject, content, reducerContext(get().userLevel, newId));
    }
    if (!personal) return routeSharedEdit(projectId, reducer, args, reducerContext(get().userLevel, newId));
    if (!useCollaborationStore.getState().sharedMeta[projectId]?.isOwner) return;
  }
  const before = get().projects.find(project => project.id === projectId);
  if (!before) return;
  const reduction = reducer(before, args, reducerContext(get().userLevel, newId));
  if (reduction.project !== before) {
    set(state => ({ projects: state.projects.map(project => project.id === projectId ? reduction.project : project) }));
  }
  publishReduction(set, projectId, before, reduction);
}

export const useProjectStore = create<ProjectState>()(
  persist<ProjectState, [], [], ProjectStorePersisted>(
    (set, get) => ({
      projects: [],
      folders: [],
      workspaces: [],
      templates: [],
      userLevel: 1,
      deleted: {},
      initializedUserId: null,
      cachedUserId: null,

      // Load-on-login from the user_projects blob, keyed by user id (re-runs on
      // account switch). Cloud-wins (atomic snapshot replace) when present; else
      // keep this user's local data (or reset on a different account). Guarded so
      // the store subscription doesn't echo the load back to cloud.
      initialize: () => dedupeInit('user_projects', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('user_projects', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const { data, error } = await supabase
            .from(USER_PROJECTS_TABLE)
            .select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const row = (data ?? [])[0];
          set((state) => {
            const cacheIsOurs =
              state.cachedUserId === null || state.cachedUserId === user.id;
            if (row && row.data && Array.isArray(row.data.projects)) {
              // Item-level merge (newer updatedAt wins) per collection, minus
              // anything a still-live tombstone shadows.
              const arr = (v: any) => (Array.isArray(v) ? v : []);
              const deleted = pruneTombstones(mergeTombstones(
                cacheIsOurs ? state.deleted : {}, readTombstones(row.data)));
              // Milestones are tasks now: a blob written by an older build still carries them.
              const projects = convertLegacyMilestonesIn(mergeProjects(cacheIsOurs ? state.projects : [], row.data.projects, deleted));
              const folders = mergeCollection(cacheIsOurs ? state.folders : [], arr(row.data.folders), deleted);
              const workspaces = mergeCollection(cacheIsOurs ? state.workspaces : [], arr(row.data.workspaces), deleted);
              // ⚠️ Same rule as the sync `build` — a collection left out here
              // arrives empty and the local copy is thrown away.
              const templates = mergeCollection(cacheIsOurs ? state.templates : [], arr(row.data.templates), deleted);
              return {
                // Reuse the previous reference on a no-op merge so the
                // subscription doesn't fire a pointless write every boot.
                projects: sameRecords(projects, state.projects) && projects.every(project => {
                  const previous = state.projects.find(item => item.id === project.id);
                  return !project.sharedMirror && !project.privateSince && !previous?.sharedMirror && !previous?.privateSince || canonical(project) === canonical(previous);
                }) ? state.projects : projects,
                folders: sameRecords(folders, state.folders) ? state.folders : folders,
                workspaces: sameRecords(workspaces, state.workspaces) ? state.workspaces : workspaces,
                templates: sameRecords(templates, state.templates) ? state.templates : templates,
                deleted: sameTombstones(deleted, state.deleted) ? state.deleted : deleted,
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }
            return cacheIsOurs
              ? { initializedUserId: user.id, cachedUserId: user.id }
              // ⚠️ `templates: []` — the cache belongs to a DIFFERENT user, so
              // leaving them would show one account's saved templates to another.
              : { projects: [], folders: [], workspaces: [], templates: [], deleted: {}, initializedUserId: user.id, cachedUserId: user.id };
          });
          projectSync.writeBack(row?.data);
          markCloudFresh('user_projects', user.id);
        } catch (error) {
          console.error('Failed to initialize projects from Supabase:', error);
        }
      }),

      /**
       * Project → template. The inverse of `buildTemplateStructure`.
       *
       * ⚠️⚠️ TASKS ARE GROUPED BY `phaseId`, NOT BY ARRAY ORDER. `Project.tasks`
       * is one flat list across every phase, so slicing it positionally would
       * scatter tasks into the wrong phases — and the result would still look
       * plausible, which is what makes it dangerous.
       * ⚠️ A MILESTONE IS A FLAGGED TASK (2026-09-16), so it belongs to a phase and
       * saves as that phase's `milestone` gate. A phase with several flagged tasks
       * keeps the first as the gate; the rest stay ordinary template tasks, because
       * `ProjectTemplate` allows exactly one gate per phase.
       */
      saveProjectAsTemplate: (projectId, name) => {
        const project = get().projects.find(p => p.id === projectId);
        if (!project) return null;

        const byPhase = new Map<string, typeof project.tasks>();
        for (const task of project.tasks ?? []) {
          if (!byPhase.has(task.phaseId)) byPhase.set(task.phaseId, []);
          byPhase.get(task.phaseId)!.push(task);
        }

        const phases = [...(project.phases ?? [])]
          .sort((a, b) => a.order - b.order)
          .map(phase => {
            const ordered = [...(byPhase.get(phase.id) ?? [])].sort((a, b) => a.order - b.order);
            const gate = ordered.find(task => task.isMilestone);
            const tasks = ordered
              .filter(task => task !== gate)
              .map(task => ({
                title: task.title,
                ...(task.subTasks?.length
                  ? { subTasks: task.subTasks.map(st => st.title) }
                  : {}),
              }));
            return {
              title: phase.title,
              ...(phase.description ? { objective: phase.description } : {}),
              ...(tasks.length ? { tasks } : {}),
              ...(gate ? { milestone: gate.title } : {}),
            };
          });

        const now = new Date().toISOString();
        const template: UserProjectTemplate = {
          id: crypto.randomUUID(),
          name: (name ?? project.title).trim() || project.title,
          ...(project.description ? { description: project.description } : {}),
          ...(project.category ? { category: project.category } : {}),
          phases,
          // Flagged tasks whose phase no longer exists have nowhere to be a gate, so
          // they ride at template level rather than being dropped.
          ...(() => {
            const phaseIds = new Set((project.phases ?? []).map(phase => phase.id));
            const orphanGates = (project.tasks ?? []).filter(task => task.isMilestone && !phaseIds.has(task.phaseId));
            return orphanGates.length ? { milestones: orphanGates.map(task => task.title) } : {};
          })(),
          createdAt: now,
          updatedAt: now,
        };

        set(state => ({ templates: [...state.templates, template] }));
        return template.id;
      },

      // ⚠️ Tombstoned, not just filtered out. Without it the union-on-write
      // merge resurrects the template from any device that still has it.
      deleteUserTemplate: (templateId) => set(state => ({
        templates: state.templates.filter(t => t.id !== templateId),
        deleted: tombstone(state.deleted, templateId),
      })),

      renameUserTemplate: (templateId, name) => set(state => ({
        templates: state.templates.map(t =>
          t.id === templateId
            // ⚠️ `updatedAt` must move, or the merge treats the rename as older
            // than the cloud copy and silently reverts it.
            ? { ...t, name: name.trim() || t.name, updatedAt: new Date().toISOString() }
            : t),
      })),

      setUserLevel: (level) => set({ userLevel: level }),

      addProject: (projectData) => {
        // ⚠️ A caller-supplied set REPLACES the defaults rather than extending
        // them — a templated project must not carry Initiation/Research/… under
        // its own phases.
        const phases: ProjectPhase[] = projectData.phases ?? DEFAULT_PHASES.map((phase, index) => ({
          ...phase,
          id: crypto.randomUUID(),
          order: index,
          collapsed: false
        }));

        const project: Project = {
          ...projectData,
          id: crypto.randomUUID(),
          progress: 0,
          // ⚠️⚠️ These three come AFTER the spread on purpose: `projectData` may
          // carry them and they must be normalised, not passed through raw.
          tasks: projectData.tasks ?? [],
          phases,
          /**
           * 🐛 THIS WAS HARDCODED `[]` AND SILENTLY DISCARDED WHAT THE CALLER
           * PASSED. `milestones` is part of the parameter type and
           * `NewProjectModal` has always sent it — harmlessly, because it sent
           * an empty array. A template sends real ones, and they vanished.
           */
          milestones: projectData.milestones ?? [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        set((state) => ({
          projects: [...state.projects, project],
        }));

        // Sync is handled by the store-level subscribe (blob pattern).
        return project.id;
      },

      updateProject: (id, updates) => {
        return applyProjectReducer(set, get, id, reducers.updateProject, updates);
      },

      deleteProject: (id) => {
        if (isSharedProject(id)) return stopSharing(id, true).then(ok => {
          if (ok) set(state => ({ projects: state.projects.filter(project => project.id !== id), deleted: tombstone(state.deleted, id) }));
        });
        // ⚠️ Needs a tombstone — writes are a union now, so a bare filter would
        // be undone by the next merge against the cloud copy.
        set((state) => ({
          projects: state.projects.filter((project) => project.id !== id),
          deleted: tombstone(state.deleted, id),
        }));
      },

      archiveProject: (id) => {
        return applyProjectReducer(set, get, id, reducers.archiveProject, undefined);
      },

      // Inverse of archiveProject; no UI caller yet.
      restoreProject: (id) => {
        return applyProjectReducer(set, get, id, reducers.restoreProject, undefined);
      },

      // Phase management
      addPhase: (projectId, phaseData) => {
        return applyProjectReducer(set, get, projectId, reducers.addPhase, phaseData, crypto.randomUUID());
      },

      updatePhase: (projectId, phaseId, updates) => {
        return applyProjectReducer(set, get, projectId, reducers.updatePhase, { phaseId, updates });
      },

      deletePhase: (projectId, phaseId) => {
        return applyProjectReducer(set, get, projectId, reducers.deletePhase, { phaseId });
      },

      reorderPhases: (projectId, sourceIndex, targetIndex) => {
        return applyProjectReducer(set, get, projectId, reducers.reorderPhases, { sourceIndex, targetIndex });
      },

      togglePhaseCollapse: (projectId, phaseId) => {
        return applyProjectReducer(set, get, projectId, reducers.togglePhaseCollapse, { phaseId });
      },

      // Task management
      addTask: (projectId, taskData) => {
        return applyProjectReducer(set, get, projectId, reducers.addTask, taskData, crypto.randomUUID());
      },

      updateTask: (projectId, taskId, updates) => {
        return applyProjectReducer(set, get, projectId, reducers.updateTask, { taskId, updates });
      },

      toggleTask: (projectId, taskId) => {
        return applyProjectReducer(set, get, projectId, reducers.toggleTask, { taskId });
      },

      updateTaskStatus: (projectId, taskId, status) => {
        console.log(`ProjectStore: Updating task ${taskId} to status: ${status}`);
        return applyProjectReducer(set, get, projectId, reducers.updateTaskStatus, { taskId, status });
      },

      moveTask: (projectId, taskId, phaseId, status) => {
        return applyProjectReducer(set, get, projectId, reducers.moveTask, { taskId, phaseId, status });
      },

      reorderTasks: (projectId, phaseId, status, sourceIndex, targetIndex) => {
        return applyProjectReducer(set, get, projectId, reducers.reorderTasks, { phaseId, status, sourceIndex, targetIndex });
      },

      deleteTask: (projectId, taskId) => {
        return applyProjectReducer(set, get, projectId, reducers.deleteTask, { taskId });
      },

      updateSubTask: (projectId, taskId, subTaskId, updates) => {
        return applyProjectReducer(set, get, projectId, reducers.updateSubTask, { taskId, subTaskId, updates });
      },

      addSubTask: (projectId, taskId, title) => {
        return applyProjectReducer(set, get, projectId, reducers.addSubTask, { taskId, title }, crypto.randomUUID());
      },

      toggleSubTask: (projectId, taskId, subTaskId) => {
        return applyProjectReducer(set, get, projectId, reducers.toggleSubTask, { taskId, subTaskId });
      },

      deleteSubTask: (projectId, taskId, subTaskId) => {
        return applyProjectReducer(set, get, projectId, reducers.deleteSubTask, { taskId, subTaskId });
      },



      getTasksByPhaseAndStatus: (projectId, phaseId, status) => {
        const project = get().projects.find(p => p.id === projectId);
        if (!project) return [];
        
        return project.tasks
          .filter(task => task.phaseId === phaseId && task.status === status)
          .sort((a, b) => a.order - b.order);
      },

      // Nested projects
      convertTaskToProject: (projectId, taskId) => {
        const state = get();
        const parentProject = state.projects.find(p => p.id === projectId);
        if (!parentProject) return null;

        const task = parentProject.tasks.find(t => t.id === taskId);
        if (!task) return null;

        const newProjectId = crypto.randomUUID();
        const phases: ProjectPhase[] = DEFAULT_PHASES.map((phase, index) => ({
          ...phase,
          id: crypto.randomUUID(),
          order: index,
          collapsed: false
        }));

        const newProject: Project = {
          id: newProjectId,
          title: task.title,
          description: task.description || '',
          category: parentProject.category,
          status: 'active',
          startDate: formatDateToLocal(new Date()),
          dueDate: parentProject.dueDate,
          priority: task.priority,
          complexity: parentProject.complexity,
          progress: 0,
          tasks: [],
          phases,
          milestones: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          tags: parentProject.tags,
          parentProjectId: projectId,
          sourceTaskId: taskId,
          folderId: parentProject.folderId,
          workspaceId: parentProject.workspaceId,
        };

        const reduction = reducers.convertTaskToProject(parentProject, { taskId, newProjectId }, reducerContext(state.userLevel));
        set(s => ({ projects: [...s.projects.map(p => p.id === projectId ? reduction.project : p), newProject] }));
        publishReduction(set, projectId, parentProject, reduction);

        return newProjectId;
      },

      getChildProjects: (projectId) => {
        return get().projects.filter(p => p.parentProjectId === projectId);
      },

      getParentProject: (projectId) => {
        const project = get().projects.find(p => p.id === projectId);
        if (!project?.parentProjectId) return undefined;
        return get().projects.find(p => p.id === project.parentProjectId);
      },

      // Folder management
      addFolder: (folderData) => {
        const folder: ProjectFolder = {
          ...folderData,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => ({ folders: [...state.folders, folder] }));
      },

      updateFolder: (folderId, updates) => {
        set((state) => ({
          folders: state.folders.map(f =>
            f.id === folderId
              ? { ...f, ...updates, updatedAt: new Date().toISOString() }
              : f
          ),
        }));
      },

      deleteFolder: (folderId) => {
        // ⚠️ Needs a tombstone — writes are a union now, so a bare filter would
        // be undone by the next merge against the cloud copy.
        set((state) => ({
          deleted: tombstone(state.deleted, folderId),
          folders: state.folders.filter(f => f.id !== folderId),
          projects: state.projects.map(p =>
            p.folderId === folderId ? reducers.clearProjectFolder(p, undefined, reducerContext(state.userLevel)).project : p
          ),
        }));
      },

      moveProjectToFolder: (projectId, folderId) => {
        return applyProjectReducer(set, get, projectId, reducers.moveProjectToFolder, { folderId });
      },

      getProjectsByFolder: (folderId) => {
        return get().projects.filter(p => p.folderId === folderId);
      },

      getFolderMetrics: (folderId) => {
        const projects = get().projects.filter(p => p.folderId === folderId);
        const now = new Date().toISOString();
        return {
          totalProjects: projects.length,
          activeProjects: projects.filter(p => p.status === 'active').length,
          completedProjects: projects.filter(p => p.status === 'completed').length,
          overdueProjects: projects.filter(p => p.status === 'active' && p.dueDate && p.dueDate < now).length,
          overallProgress: projects.length > 0
            ? Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length)
            : 0,
        };
      },

      getFoldersByWorkspace: (workspaceId) => {
        return get().folders.filter(f => f.workspaceId === workspaceId);
      },

      // Workspace management
      addWorkspace: (workspaceData) => {
        const workspace: ProjectWorkspace = {
          ...workspaceData,
          id: crypto.randomUUID(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        set((state) => ({ workspaces: [...state.workspaces, workspace] }));
      },

      updateWorkspace: (workspaceId, updates) => {
        set((state) => ({
          workspaces: state.workspaces.map(w =>
            w.id === workspaceId
              ? { ...w, ...updates, updatedAt: new Date().toISOString() }
              : w
          ),
        }));
      },

      deleteWorkspace: (workspaceId) => {
        // ⚠️ Needs a tombstone — writes are a union now, so a bare filter would
        // be undone by the next merge against the cloud copy.
        set((state) => ({
          deleted: tombstone(state.deleted, workspaceId),
          workspaces: state.workspaces.filter(w => w.id !== workspaceId),
          folders: state.folders.map(f =>
            f.workspaceId === workspaceId ? { ...f, workspaceId: undefined } : f
          ),
          projects: state.projects.map(p =>
            p.workspaceId === workspaceId ? reducers.clearProjectWorkspace(p, undefined, reducerContext(state.userLevel)).project : p
          ),
        }));
      },

      moveProjectToWorkspace: (projectId, workspaceId) => {
        return applyProjectReducer(set, get, projectId, reducers.moveProjectToWorkspace, { workspaceId });
      },

      moveFolderToWorkspace: (folderId, workspaceId) => {
        set((state) => ({
          folders: state.folders.map(f =>
            f.id === folderId
              ? { ...f, workspaceId, updatedAt: new Date().toISOString() }
              : f
          ),
        }));
      },

      getProjectsByWorkspace: (workspaceId) => {
        return get().projects.filter(p => p.workspaceId === workspaceId);
      },

      getWorkspaceMetrics: (workspaceId) => {
        const projects = get().projects.filter(p => p.workspaceId === workspaceId);
        const now = new Date().toISOString();
        const totalTasks = projects.reduce((sum, p) => sum + p.tasks.length, 0);
        const completedTasks = projects.reduce(
          (sum, p) => sum + p.tasks.filter(t => t.status === 'done').length, 0
        );

        return {
          totalProjects: projects.length,
          activeProjects: projects.filter(p => p.status === 'active').length,
          completedProjects: projects.filter(p => p.status === 'completed').length,
          overdueProjects: projects.filter(p => p.status === 'active' && p.dueDate && p.dueDate < now).length,
          averageProgress: projects.length > 0
            ? Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length)
            : 0,
          taskCompletionRate: totalTasks > 0
            ? Math.round((completedTasks / totalTasks) * 100)
            : 0,
          overallProgressScore: projects.length > 0
            ? Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length)
            : 0,
        };
      },

      getActiveProjects: () => {
        return get().projects.filter((project) => project.status === 'active');
      },

      getArchivedProjects: () => {
        return get().projects.filter(
          (project) => project.status === 'archived' || project.status === 'completed'
        );
      },

      getRootProjects: () => {
        return get().projects.filter(p => !p.parentProjectId);
      },
    }),
    {
      name: 'projects-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 6,
      partialize: (state): ProjectStorePersisted => ({
        projects: state.projects,
        folders: state.folders,
        workspaces: state.workspaces,
        templates: state.templates,
        // Must persist: a tombstone that dies on reload lets the next merge
        // resurrect the record it was suppressing.
        deleted: state.deleted,
        userLevel: state.userLevel,
        cachedUserId: state.cachedUserId,
      }),
      migrate: (persistedState: any, version: number) => {
        let migratedProjects = persistedState.projects || [];

        if (version < 3) {
          migratedProjects = migratedProjects.map((project: any) => {
            const phases = DEFAULT_PHASES.map((phase, index) => ({
              ...phase,
              id: crypto.randomUUID(),
              order: index,
              collapsed: false
            }));

            const migratedTasks = project.tasks?.map((task: any, index: number) => {
              const phaseId = phases[0]?.id || crypto.randomUUID();
              const status: TaskStatus = task.completed ? 'done' : 'todo';
              return { ...task, phaseId, status, order: index };
            }) || [];

            return {
              ...project,
              phases,
              tasks: migratedTasks,
              progress: calculateProjectProgress(phases, migratedTasks)
            };
          });
        }

        if (version < 4) {
          migratedProjects = migratedProjects.map((project: any) => ({
            ...project,
            title: project.title || project.name || 'Untitled Project',
            priority: project.priority || 'medium',
            complexity: project.complexity || 'medium',
            startDate: project.startDate || new Date().toISOString(),
            tags: Array.isArray(project.tags) ? project.tags.filter((tag: any) => tag && typeof tag === 'string') : []
          }));
        }

        // v6: milestones became a task flag (owner call 2026-09-16).
        if (version < 6) {
          migratedProjects = migratedProjects.map((project: any) =>
            convertLegacyMilestones({ ...project, tasks: project.tasks ?? [], phases: project.phases ?? [], milestones: project.milestones ?? [] }));
        }

        if (version < 5) {
          migratedProjects = migratedProjects.map((project: any) => ({
            ...project,
            parentProjectId: project.parentProjectId || undefined,
            sourceTaskId: project.sourceTaskId || undefined,
            folderId: project.folderId || undefined,
            workspaceId: project.workspaceId || undefined,
          }));
        }

        return {
          projects: migratedProjects,
          folders: persistedState.folders || [],
          workspaces: persistedState.workspaces || [],
          userLevel: persistedState.userLevel || 1,
        } as unknown as ProjectState;
      },
    }
  )
);

// Auto-sync to Supabase whenever projects/folders/workspaces change (covers all
// mutations). Ref-equality skips non-data changes (e.g. userLevel). No suppression
// flag: initialize() reuses references on a no-op merge, and a write-back when it
// DID change something is what makes the two sides converge.
useProjectStore.subscribe((state, prev) => {
  if (swappingAccount()) return; // a change of account swaps the saved copy in — not an edit
  if (
    state.projects === prev.projects &&
    state.folders === prev.folders &&
    state.workspaces === prev.workspaces &&
    state.templates === prev.templates &&
    state.deleted === prev.deleted
  ) return;
  projectSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useProjectStore);
/** Unshare needs positive transport evidence: flush alone deliberately swallows errors. */
export async function persistPrivateProject(project: Project): Promise<void> {
  useProjectStore.setState(state => ({ projects: [...state.projects.filter(item => item.id !== project.id), project] }));
  projectSync.schedule();
  await projectSync.flush();
  const uploaded = await projectTransport.read();
  const projects = uploaded?.data?.projects as Project[] | undefined;
  const copy = projects?.find(item => item.id === project.id);
  if (!copy || canonical(copy) !== canonical(project)) throw new Error('The private copy upload could not be confirmed. Nothing was deleted.');
}

/** An absent shared row is not enough: require the owner's uploaded private copy. */
export async function adoptUnsharedCopies(sharedIds: Set<string>): Promise<void> {
  const missing = useProjectStore.getState().projects.filter(project => (project.sharedMirror || project.privateSince?.pending) && !sharedIds.has(project.id) && !lifecycleInProgress(project.id));
  if (!missing.length) return;
  for (const project of missing) setSharedCopyWaiting(project.id, true);
  const uploaded = await projectTransport.read();
  const copies = uploaded?.data?.projects as Project[] | undefined;
  for (const project of missing) {
    const copy = copies?.find(item => item.id === project.id && !item.sharedMirror);
    if (copy) {
      useProjectStore.setState(state => ({ projects: state.projects.map(item => item.id === copy.id ? copy : item) }));
      finalizePrivateProject(project.id);
      setSharedCopyWaiting(project.id, false);
    }
  }
}

/** A prepared copy survives a crash between upload and DELETE; row absence finalises it. */
export function finalizePrivateProject(id: string): void {
  useProjectStore.setState(state => ({ projects: state.projects.map(project => {
    if (project.id !== id || !project.privateSince?.pending) return project;
    const { pending, ...fact } = project.privateSince; void pending;
    return { ...project, privateSince: fact };
  }) }));
  projectSync.schedule();
}
